import type { SoftphoneCollapsibleItemsProps } from './index.types';
import { AnimatePresence, motion } from 'framer-motion';
import { useKeyboardNavigation } from 'Hooks/keyboardNavigation';
import { ChevronRight } from 'lucide-react';
import React, { useId, useEffect, useRef } from 'react';
import { useClickOutside } from '~/hooks/clickOutside';
import { Collapsible, CollapsibleTrigger } from '~/lib/ui/collapsible';
import { cn } from '~/lib/utils';
import { collapsibleContentVariants } from './animations';
import { baseClasses, triggerClasses } from './index.styles';

/**
 * SoftphoneCollapsibleItems — Generic collapsible component for grouping calls.
 *
 * Features:
 * - Collapsible header with icon, title, optional subtitle, and chevron
 * - Radix UI Collapsible for accessibility
 * - Animated chevron rotation with Tailwind CSS
 * - Keyboard navigation via useKeyboardNavigation hook
 *   (ArrowUp, ArrowDown, Enter, Space, Home, End, Escape)
 * - ARIA attributes: aria-expanded, aria-controls, aria-label
 * - Focus management: clones children with ref, tabIndex, and data-focused
 */
export const SoftphoneCollapsibleItems = ({
  title,
  subtitle,
  icon: Icon,
  isExpanded,
  onToggle,
  closeCollapsible,
  boundaryRef,
  listAriaLabel,
  children,
  itemCount,
  onItemSelect,
  testID,
  dataTestId,
}: SoftphoneCollapsibleItemsProps): React.ReactElement => {
  const listId = useId();
  const collapsibleRef = useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<Map<number, HTMLElement>>(new Map());

  const componentTestId =
    dataTestId ?? (testID ? `${testID}-call-group` : undefined);

  useClickOutside(
    collapsibleRef,
    () => closeCollapsible?.(),
    isExpanded,
    boundaryRef
  );

  const { focusedIndex, setFocusedIndex, handleKeyDown } =
    useKeyboardNavigation({
      isExpanded,
      itemCount,
      onSelect: (index) => {
        onItemSelect?.(index);
      },
    });

  useEffect(() => {
    const hasValidFocusedIndex =
      isExpanded &&
      typeof focusedIndex === 'number' &&
      focusedIndex >= 0 &&
      focusedIndex < itemCount;

    if (hasValidFocusedIndex) {
      const element = itemRefs.current.get(focusedIndex);
      element?.focus();
    }
  }, [focusedIndex, itemCount, isExpanded]);

  const ariaLabel = `${title}${subtitle ? ' ' + subtitle : ''}. Click to ${
    isExpanded ? 'collapse' : 'expand'
  }`;

  return (
    <div ref={collapsibleRef}>
      <Collapsible
        open={isExpanded}
        onOpenChange={onToggle}
        data-testid={componentTestId}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            aria-expanded={isExpanded}
            aria-controls={listId}
            aria-label={ariaLabel}
            className={cn(baseClasses, triggerClasses, 'relative z-[51]')}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' && isExpanded) {
                e.preventDefault();
                setFocusedIndex(0);
              }
            }}
          >
            <div className="flex items-center gap-[8px] text-left">
              <Icon className="size-[24px] text-foreground flex-shrink-0" />
              <div className="flex flex-col gap-[4px] min-w-0">
                <span className="text-[14px] text-foreground font-semibold truncate leading-[19px]">
                  {title}
                </span>
                {subtitle && (
                  <span className="text-xs text-foreground font-normal leading-[16px]">
                    {subtitle}
                  </span>
                )}
              </div>
            </div>

            <ChevronRight
              className={cn(
                'size-[24px] text-foreground transition-transform duration-300 ease-in-out',
                isExpanded ? 'rotate-90' : 'rotate-0'
              )}
            />
          </button>
        </CollapsibleTrigger>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              variants={collapsibleContentVariants}
              className="absolute top-full left-0 right-0 z-50 overflow-hidden shadow-md"
            >
              <div className="bg-muted border-t border-border">
                <ul
                  id={listId}
                  aria-label={listAriaLabel}
                  aria-live="polite"
                  aria-atomic="false"
                  className="scroll-area-content flex flex-col max-h-[165px] overflow-y-auto"
                  onKeyDown={handleKeyDown}
                >
                  {React.Children.map(children, (child, index) => {
                    if (!React.isValidElement(child)) return child;

                    const isFocused = focusedIndex === index;

                    return (
                      <li key={child.key ?? index}>
                        {React.cloneElement(
                          child as React.ReactElement<Record<string, unknown>>,
                          {
                            ref: (el: HTMLElement | null) => {
                              if (el) {
                                itemRefs.current.set(index, el);
                              } else {
                                itemRefs.current.delete(index);
                              }
                            },
                            tabIndex: isFocused ? 0 : -1,
                            'data-focused': isFocused,
                          }
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Collapsible>
    </div>
  );
};

SoftphoneCollapsibleItems.displayName = 'SoftphoneCollapsibleItems';
